import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check, Captions } from "lucide-react";
import { Button } from "./ui/button";
import { CaptionStyleSelector } from "./CaptionStyleSelector";
import { captionsAPI } from "../lib/api";
import { toast } from "sonner";

export function ApplyCaptionsModal({ 
  open, 
  onClose, 
  videoId, 
  currentStyle = null,
  onCaptionsApplied 
}) {
  const [selectedStyle, setSelectedStyle] = useState(currentStyle || "classic");
  const [isApplying, setIsApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const handleApply = async () => {
    if (!videoId) return;
    
    setIsApplying(true);
    try {
      const response = await captionsAPI.applyToVideo(videoId, selectedStyle);
      setApplied(true);
      toast.success(`${selectedStyle.charAt(0).toUpperCase() + selectedStyle.slice(1)} captions applied!`);
      
      if (onCaptionsApplied) {
        onCaptionsApplied(response.data);
      }
      
      // Auto close after success
      setTimeout(() => {
        onClose();
        setApplied(false);
      }, 1500);
    } catch (error) {
      console.error("Failed to apply captions:", error);
      toast.error(error.response?.data?.detail || "Failed to apply captions");
    } finally {
      setIsApplying(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Captions className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Apply Captions</h2>
                <p className="text-white/50 text-sm">Choose a caption style for your video</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-caption-modal"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5">
            <CaptionStyleSelector
              value={selectedStyle}
              onChange={setSelectedStyle}
              showPreview={true}
            />

            {/* Info text */}
            <p className="text-white/50 text-sm text-center">
              Captions will be burned into your video. This may take a minute.
            </p>
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-white/10 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/20 text-white/70 hover:bg-white/10"
              disabled={isApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={isApplying || applied}
              className="flex-1 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 text-white font-semibold"
              data-testid="apply-captions-btn"
            >
              {applied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Applied!
                </>
              ) : isApplying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Captions className="w-4 h-4 mr-2" />
                  Apply Captions
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ApplyCaptionsModal;
